<script setup lang="ts">
import { PageEnum } from "@/enums";
import { EventTypeEnum } from "@/models";
import {
    useCustomerStore,
    useDataLayerStore,
    useGlobalStore,
    useQuoteStore,
} from "@/stores";
import JourneyLayout from "@/components/layout/JourneyLayout/JourneyLayout.vue";
import AsideView from "@/views/AsideView.vue";
import CoverSummaryBox from "@/components/form/coverSummary/CoverSummaryBox.vue";
import PageHeading from "@/components/general/PageHeading/PageHeading.vue";
import Button from "@/components/general/Button/Button.vue";
import DefaqtoLogo from "@/components/general/DefaqtoLogo/DefaqtoLogo.vue";
import CoverBoxes from "@/components/form/coverSummary/CoverBoxes.vue";
import Incentives from "@/components/general/Incentives/Incentives.vue";

// Stores
const dataLayer = useDataLayerStore();
const globalStore = useGlobalStore();
const quoteStore = useQuoteStore();
const customerStore = useCustomerStore();

const emit = defineEmits<{
    submitSection: [currentPage: PageEnum, nextPage: PageEnum];
}>();

const submitSection = (currentPage: PageEnum, nextPage: PageEnum) => {
    dataLayer.triggerEvent(
        EventTypeEnum.PageInteraction,
        "button|Continue to your Details"
    );
    emit("submitSection", currentPage, nextPage);
};

defineExpose({ submitSection });
</script>

<template>
    <JourneyLayout>
        <template #main>
            <div :id="PageEnum.CoverSummary">
                <PageHeading>Your Cover Summary</PageHeading>
                <div class="image-div rounded-corners">
                    <img
                        src="@img/summary-hero.png"
                        alt="cover summary image"
                    />
                </div>
                <div class="cover-box-container">
                    <CoverBoxes
                        title="Your cash sum"
                        :value="globalStore.formatNumber(quoteStore.mySA, true)"
                    />
                    <CoverBoxes
                        title="You will pay"
                        :value="`${globalStore.formatNumber(quoteStore.myPPM, true)} per month`"
                    />
                    <CoverBoxes
                        title="Max payment term"
                        :value="`${90 - customerStore.age} years`"
                    />
                </div>
                <CoverSummaryBox />
                <div>
                    <Button
                        id="submitCoverSummaryTop"
                        btn-type="primary"
                        class="continue"
                        @click="
                            submitSection(
                                PageEnum.CoverSummary,
                                PageEnum.YourDetails
                            )
                        "
                    >
                        Continue
                    </Button>
                </div>
                <div class="component-footer">
                    <Incentives class="incentive-mobile" />
                    <DefaqtoLogo class="defaqto-mobile" />
                </div>
            </div>
        </template>
        <template #aside>
            <AsideView :show-defaqto="true" class="aside" />
        </template>
    </JourneyLayout>
</template>

<style scoped>
.aside {
    display: none;
    margin-top: 85px;
}

.image-div {
    margin-top: 20px;
    margin-bottom: 16px;
    width: 100%;
}

.defaqto-mobile {
    margin-top: 1.2rem;
    display: block;
    justify-self: center;
}

.cover-boxes-container {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    gap: 0.5rem;
    align-items: stretch;
}

.cover-box-container {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    gap: 0.5rem;
    align-items: stretch;
}

.cover-box-container > * {
    flex: 1;
    display: flex;
}

@media screen and (width >= 585px) {
    .defaqto-mobile {
        justify-self: center;
    }
}

@media screen and (width <= 993px) {
    .cover-box-container {
        display: block;
    }

    .component-footer {
        display: grid;
        justify-content: center;
    }
}

@media screen and (width >= 993px) {
    .aside {
        display: block;
    }

    .defaqto-mobile {
        display: none;
    }

    .cover-box-container {
        display: flex;
        flex-direction: row;
    }
}
</style>
